import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import toast from 'react-hot-toast';
import './CSVImport.css';

const CSVImport = ({ onImportComplete, onClose }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importProgress, setImportProgress] = useState(0);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setIsUploading(true);
    setImportProgress(0);

    try {
      // Create FormData
      const formData = new FormData();
      formData.append('csvFile', file);

      // Upload and preview
      const response = await axios.post('/api/expenses/import/preview', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setImportProgress(progress);
        }
      });

      if (response.data.success) {
        setPreviewData(response.data.data);
        toast.success('CSV uploaded successfully! Review the preview below.');
      }
    } catch (error) {
      console.error('CSV upload error:', error);
      toast.error('Failed to upload CSV file');
    } finally {
      setIsUploading(false);
      setImportProgress(0);
    }
  }, []);

  const handleImport = async () => {
    if (!previewData) return;

    setIsUploading(true);
    setImportProgress(0);

    try {
      const response = await axios.post('/api/expenses/import/process', {
        transactions: previewData.transactions,
        mapping: previewData.mapping
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setImportProgress(progress);
        }
      });

      if (response.data.success) {
        toast.success(`Successfully imported ${response.data.importedCount} transactions!`);
        onImportComplete();
        onClose();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import transactions');
    } finally {
      setIsUploading(false);
      setImportProgress(0);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv']
    },
    multiple: false
  });

  return (
    <div className="csv-import-overlay">
      <div className="csv-import-modal">
        <div className="csv-import-header">
          <h2>📁 Import Bank Transactions</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <div className="csv-import-content">
          {/* Upload Section */}
          <div className="upload-section">
            <div 
              {...getRootProps()} 
              className={`dropzone ${isDragActive ? 'drag-active' : ''} ${isUploading ? 'uploading' : ''}`}
            >
              <input {...getInputProps()} />
              {isUploading ? (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${importProgress}%` }}
                    ></div>
                  </div>
                  <p>Uploading... {importProgress}%</p>
                </div>
              ) : (
                <div className="upload-content">
                  <div className="upload-icon">📁</div>
                  <h3>Drop your CSV file here</h3>
                  <p>or click to browse</p>
                  <p className="supported-banks">
                    Supports: USAA, Marcus, Schwab, and other bank CSV formats
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Preview Section */}
          {previewData && (
            <div className="preview-section">
              <h3>📊 Import Preview</h3>
              <div className="preview-stats">
                <div className="stat">
                  <span className="stat-label">Total Transactions:</span>
                  <span className="stat-value">{previewData.transactions.length}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Date Range:</span>
                  <span className="stat-value">
                    {previewData.dateRange.start} to {previewData.dateRange.end}
                  </span>
                </div>
                <div className="stat">
                  <span className="stat-label">Total Amount:</span>
                  <span className="stat-value">${previewData.totalAmount.toFixed(2)}</span>
                </div>
              </div>

              <div className="preview-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Category</th>
                      <th>Property</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.transactions.slice(0, 10).map((transaction, index) => (
                      <tr key={index}>
                        <td>{transaction.date}</td>
                        <td>{transaction.description}</td>
                        <td className={transaction.amount < 0 ? 'negative' : 'positive'}>
                          ${Math.abs(transaction.amount).toFixed(2)}
                        </td>
                        <td>{transaction.category}</td>
                        <td>{transaction.property || 'None'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewData.transactions.length > 10 && (
                  <p className="preview-note">
                    Showing first 10 transactions of {previewData.transactions.length} total
                  </p>
                )}
              </div>

              <div className="import-actions">
                <button 
                  onClick={handleImport}
                  disabled={isUploading}
                  className="import-button"
                >
                  {isUploading ? 'Importing...' : 'Import Transactions'}
                </button>
                <button 
                  onClick={() => setPreviewData(null)}
                  className="cancel-button"
                  disabled={isUploading}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CSVImport;


